import { useMutation, useQueryClient } from '@tanstack/react-query';
import { teamService } from '@/services/team/teamService';
import { teamKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import type { 
  InviteUserInput, 
  ArchiveMemberInput, 
  ReactivateMemberInput, 
  ResendInvitationInput,
  DeleteMemberInput,
  CancelPendingInvitationInput
} from '@/services/team/types';

export const useTeamMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const inviteUser = useMutation({
    mutationFn: (input: InviteUserInput) => teamService.inviteUser(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
      toast({
        title: 'Success',
        description: `Invitation sent to ${variables.email}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });


  const archiveMember = useMutation({
    mutationFn: (input: ArchiveMemberInput) => teamService.archiveMember(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
      toast({
        title: 'Success',
        description: 'Team member has been archived',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const reactivateMember = useMutation({
    mutationFn: (input: ReactivateMemberInput) => teamService.reactivateMember(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
      toast({
        title: 'Success',
        description: 'Team member has been reactivated',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const resendInvitation = useMutation({
    mutationFn: (input: ResendInvitationInput) => teamService.resendInvitation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
      toast({
        title: 'Success',
        description: 'Invitation resent successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMember = useMutation({
    mutationFn: (input: DeleteMemberInput) => teamService.deleteMember(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
      toast({
        title: 'Success',
        description: 'Team member has been permanently deleted',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const cancelPendingInvitation = useMutation({
    mutationFn: (input: CancelPendingInvitationInput) => teamService.cancelPendingInvitation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
      toast({
        title: 'Success',
        description: 'Pending invitation has been cancelled',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    inviteUser,
    archiveMember,
    reactivateMember,
    resendInvitation,
    deleteMember,
    cancelPendingInvitation,
  };
};
